// ============================================================
// PhysicsEngine — stable AABB collision world
// ============================================================

export class PhysicsEngine {
  constructor() {
    /** @type {import('./PhysicsBody.js').PhysicsBody[]} */
    this.bodies     = [];
    this.waterZones = [];
    this.windZones  = [];
  }

  addBody(body) {
    body.onGround = false;
    body.inWater  = false;
    this.bodies.push(body);
    return body;
  }

  removeBody(body) {
    const i = this.bodies.indexOf(body);
    if (i !== -1) this.bodies.splice(i, 1);
  }

  addWaterZone(x, y, w, h)          { this.waterZones.push({ x, y, w, h }); }
  addWindZone(x, y, w, h, fx, fy)   { this.windZones.push({ x, y, w, h, fx, fy }); }

  update(dt) {
    for (const b of this.bodies) {
      b.onGround      = false;
      b.onCeiling     = false;
      b.inWater       = false;
      b.touchedBouncy = false;
    }
    this._applyZones();
    for (const b of this.bodies) b.step(dt);
    this._resolveCollisions();
    // Update sleep state AFTER collisions (so onGround is accurate)
    for (const b of this.bodies) b._updateSleep(dt);
    this.bodies = this.bodies.filter(b => !b._destroyNextFrame);
  }

  _applyZones() {
    for (const b of this.bodies) {
      if (b.isStatic) continue;
      for (const w of this.waterZones) {
        if (rectOverlap(b.left, b.top, b.width, b.height, w.x, w.y, w.w, w.h)) {
          b.inWater = true;
          if (b.canFloat) b.ay -= 2800;
        }
      }
      for (const wz of this.windZones) {
        if (rectOverlap(b.left, b.top, b.width, b.height, wz.x, wz.y, wz.w, wz.h)) {
          b.ax += wz.fx;
          b.ay += wz.fy;
        }
      }
    }
  }

  _resolveCollisions() {
    const dynamic  = this.bodies.filter(b => !b.isStatic && !b.isTrigger);
    const solids   = this.bodies.filter(b => b.isStatic && !b.isTrigger);
    const triggers = this.bodies.filter(b => b.isTrigger);

    // ── Triggers first (no position change) ──
    for (const d of dynamic) {
      for (const t of triggers) {
        if (d.overlaps(t) && t.onTrigger) t.onTrigger(d);
      }
    }

    // ── Dynamic vs Solid statics ──
    for (const d of dynamic) {
      for (const s of solids) {
        if (!d.overlaps(s)) continue;
        resolveIntoStatic(d, s);
      }
    }

    // ── Dynamic vs Dynamic ──
    // Sleeping bodies act like statics — only the awake body moves.
    for (let i = 0; i < dynamic.length; i++) {
      for (let j = i + 1; j < dynamic.length; j++) {
        const a = dynamic[i];
        const b = dynamic[j];
        if (!a.overlaps(b)) continue;
        // Wake sleeping bodies on significant contact
        if (a.sleeping || b.sleeping) {
          const relSpeed = Math.sqrt(
            (a.vx - b.vx) ** 2 + (a.vy - b.vy) ** 2
          );
          if (relSpeed > 20) { a.wake(); b.wake(); }
          else {
            // Treat the sleeping one as static — only push the awake one out
            if (a.sleeping) resolveIntoStatic(b, a);
            else            resolveIntoStatic(a, b);
            continue;
          }
        }
        resolveDynamic(a, b);
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Static resolution — push `d` out of `s`, apply surface effects
// ─────────────────────────────────────────────────────────────
function resolveIntoStatic(d, s) {
  const overlapX = Math.min(d.right - s.left, s.right - d.left);
  const overlapY = Math.min(d.bottom - s.top, s.bottom - d.top);
  if (overlapX <= 0 || overlapY <= 0) return;

  if (overlapX < overlapY) {
    // Side collision
    const dir = d.x < s.x ? -1 : 1;
    d.x += dir * overlapX;
    if (d.vx * dir < 0) {           // only zero vx if moving INTO the wall
      d.vx = d.vx * -d.bounciness;
      if (Math.abs(d.vx) < 6) d.vx = 0;
    }
  } else {
    // Vertical collision
    const dir = d.y < s.y ? -1 : 1;
    d.y += dir * overlapY;
    if (dir === -1) {
      // Landing on top of s
      if (d.vy > 0) {
        const bounce = s.bounciness ?? d.bounciness;   // surface bounciness wins
        if (bounce > 0.5) {
          d.touchedBouncy = true;
          d.vy = -d.vy * bounce;                       // full elastic bounce
        } else {
          d.vy = d.vy * -d.bounciness;
          if (Math.abs(d.vy) < 30) d.vy = 0;
        }
        d.onGround = true;
      }
      d.vx *= materialFriction(s.material);
    } else {
      // Hit ceiling
      if (d.vy < 0) d.vy = Math.abs(d.vy) * 0.15;
      d.onCeiling = true;
    }
    if (d.onCollide) d.onCollide(s, { nx: 0, ny: dir });
    if (s.onCollide) s.onCollide(d, { nx: 0, ny: -dir });
  }
}

// ─────────────────────────────────────────────────────────────
// Dynamic vs dynamic — one-sided push based on mass ratio,
// so heavy "platform" objects barely move and don't shake.
// ─────────────────────────────────────────────────────────────
function resolveDynamic(a, b) {
  const overlapX = Math.min(a.right - b.left, b.right - a.left);
  const overlapY = Math.min(a.bottom - b.top, b.bottom - a.top);
  if (overlapX <= 0 || overlapY <= 0) return;

  // Heavier body moves proportionally less
  const totalMass  = (a.mass + b.mass) || 1;
  const aShare     = b.mass / totalMass;  // lighter body moves more
  const bShare     = a.mass / totalMass;

  if (overlapX < overlapY) {
    // Horizontal push: sepDir = +1 when a is left of b
    // a moves in -sepDir direction (left), b moves in +sepDir direction (right)
    const sepDir = a.x < b.x ? 1 : -1;
    a.x -= sepDir * overlapX * aShare;
    b.x += sepDir * overlapX * bShare;

    // Elastic-ish velocity exchange (momentum conserving)
    const relVx = a.vx - b.vx;
    if (relVx * sepDir > 0) {   // only if approaching each other
      const imp = relVx * 0.7;
      a.vx -= imp * aShare;
      b.vx += imp * bShare;
    }
  } else {
    // Vertical push
    const sepDir = a.y < b.y ? 1 : -1;
    a.y -= sepDir * overlapY * aShare;
    b.y += sepDir * overlapY * bShare;

    if (sepDir === 1) {
      // a is above b (a.y < b.y, so a lands on top of b)
      if (a.vy > 0) {
        const bounce = b.bounciness ?? 0;
        if (bounce > 0.5) {
          a.touchedBouncy = true;
          a.vy = -a.vy * bounce;
        } else {
          a.vy = 0;
        }
        a.onGround = true;
        b.vy += (a.mass / totalMass) * 30;
      }
      a.vx *= 0.88;
    } else {
      // b is above a
      if (b.vy > 0) { b.vy = 0; b.onGround = true; }
    }
    if (a.onCollide) a.onCollide(b, { nx: 0, ny: sepDir });
    if (b.onCollide) b.onCollide(a, { nx: 0, ny: -sepDir });
  }
}

// ─────────────────────────────────────────────────────────────
function rectOverlap(ax, ay, aw, ah, bx, by, bw, bh) {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

function materialFriction(material) {
  switch (material) {
    case 'ice':    return 0.98;
    case 'sticky': return 0.35;
    case 'bouncy': return 0.90;
    default:       return 0.83;
  }
}
